import React from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  LuBuilding2,
  LuCalendar,
  LuDatabase,
  LuExternalLink,
  LuFileText,
  LuPackage,
  LuPencil,
  LuScissors,
  LuTrash2,
  LuTrendingDown,
  LuTrendingUp,
} from "react-icons/lu";

import Button from "../ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  cardClass,
  cardContentFlushClass,
  creditCellClass,
  creditTextClass,
  debitCellClass,
  debitTextClass,
  mobileCardClass,
  moneyCellClass,
  tableHeadCellClass,
} from "../ui/styles";
import { formatDateTime, formatQty, partyText } from "../../static/materialLine";
import { route } from "../../static/route";
import { MaterialStockLedgerRowProps } from "../../types/materialStock";

type EntryRow = Extract<MaterialStockLedgerRowProps, { kind: "entry" }>;

const formatDate = (value: string) => moment(value).format("DD-MM-YYYY");

const formatLongDate = (value: string) =>
  moment(value).format("DD MMMM, YYYY");

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const voucherLinkClass =
  "group inline-flex items-center gap-2 text-teal-600 hover:text-teal-800 transition-colors";

const noteLineClass = "flex items-start gap-1.5 text-sm text-gray-600";

const emptyLabel = "આ મટીરીયલ ની કોઈ ખરીદી, કટીંગ ઓર્ડર કે એન્ટ્રી નથી";

// Bars come in with a purchase or an added entry, and go out with a cutting order or a taken one.
const isIn = (row: MaterialStockLedgerRowProps) =>
  row.kind === "purchase" || (row.kind === "entry" && row.type === 0);

// The bars a row brought in or sent out, and the material's stock after it. The materials module doesn't show sizes
// (a new material has the one 3660 size), so the sizes of a material made before that with more are added up.
const rowQty = (row: MaterialStockLedgerRowProps, key: "qty_in" | "qty_out") =>
  row.lines.reduce((sum, line) => sum + (Number(line[key]) || 0), 0);

const rowStock = (row: MaterialStockLedgerRowProps) =>
  row.after.reduce((sum, balance) => sum + (Number(balance.stock) || 0), 0);

// The row's kind, as the party account names its rows: an icon and a label.
const typeHead = (row: MaterialStockLedgerRowProps) => {
  if (row.kind === "entry") {
    return (
      <div className="flex items-center gap-1.5">
        {row.type === 0 ? (
          <LuTrendingUp className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <LuTrendingDown className="h-3.5 w-3.5 text-orange-600" />
        )}
        <span className="text-sm font-semibold text-gray-900">સ્ટોક વધ-ઘટ</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {row.kind === "purchase" ? (
        <LuPackage className="h-3.5 w-3.5 text-teal-600" />
      ) : (
        <LuScissors className="h-3.5 w-3.5 text-teal-600" />
      )}
      <span className="text-sm font-semibold text-teal-900">
        {row.kind === "purchase" ? "મટીરીયલ ખરીદી" : "કટીંગ ઓર્ડર"}
      </span>
    </div>
  );
};

type Props = {
  // The rows of the page being shown, newest first.
  rows: MaterialStockLedgerRowProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  canOpenPurchase: boolean;
  canOpenCutting: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (entry: EntryRow) => void;
  onDelete: (entry: EntryRow) => void;
};

// One material's movements, laid out like the party account's table: a purchase brings its bars in, a cutting order
// sends its bars out, and an entry adds or takes bars, each with the stock after it.
const MaterialStockLedger = ({
  rows,
  pageNumber,
  pageSize,
  isLoading,
  canOpenPurchase,
  canOpenCutting,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
}: Props) => {
  const actionCount = (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);
  const tableColSpan = 7 + (actionCount > 0 ? 1 : 0);

  // The record's ID and date, opening it in a new tab like the party account's purchase rows; plain for a user who may
  // not open it. An entry says what it did.
  const typeDetails = (row: MaterialStockLedgerRowProps) => {
    if (row.kind === "entry") {
      return (
        <div className="text-sm text-gray-600">
          {row.type === 0 ? "સ્ટોક ઉમેરાયો" : "સ્ટોક બાદ થયો"}
        </div>
      );
    }

    const content = (
      <>
        <span className="text-sm font-medium">ID: {row.id}</span>
        <span className="text-gray-400">•</span>
        <span className="text-sm">{formatLongDate(row.date)}</span>
      </>
    );

    const canOpen = row.kind === "purchase" ? canOpenPurchase : canOpenCutting;

    if (!canOpen) {
      return (
        <span className="inline-flex items-center gap-2 text-teal-600">
          {content}
        </span>
      );
    }

    const path =
      row.kind === "purchase"
        ? route.materialPurchaseDetail
        : route.cuttingOrderDetail;

    return (
      <Link
        to={path.replace(":id", String(row.id))}
        target="_blank"
        rel="noopener noreferrer"
        className={voucherLinkClass}
      >
        {content}
        <LuExternalLink className="h-3.5 w-3.5" />
      </Link>
    );
  };

  // The party a purchase came from or a cutting order went to, or an entry's note.
  const extraText = (row: MaterialStockLedgerRowProps) =>
    row.kind === "entry" ? row.note : row.party ? partyText(row.party) : null;

  const extraLine = (row: MaterialStockLedgerRowProps) => {
    const text = extraText(row);

    if (!text) return null;

    const Icon = row.kind === "entry" ? LuFileText : LuBuilding2;

    return (
      <div className={noteLineClass}>
        <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span className="break-words">{text}</span>
      </div>
    );
  };

  const stockAfter = (row: MaterialStockLedgerRowProps) => {
    const stock = rowStock(row);

    return (
      <span className={cx("whitespace-nowrap", stock < 0 && "text-red-600")}>
        {formatQty(stock)}
      </span>
    );
  };

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    ક્રમ
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    પ્રકાર
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>વિગતો</TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    આવ્યું
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    ગયું
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    સ્ટોક
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>તારીખ</TableHead>
                  {actionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : rows.length === 0 ? (
                <TableEmptyRow colSpan={tableColSpan} label={emptyLabel} />
              ) : (
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={`${row.kind}-${row.id}`}>
                      <TableCell className="font-medium text-center">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell className="text-center">
                        {isIn(row) ? (
                          <p className={creditTextClass}>આવ્યું</p>
                        ) : (
                          <p className={debitTextClass}>ગયું</p>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="min-w-0 space-y-1.5">
                          {typeHead(row)}
                          {typeDetails(row)}
                          {extraLine(row)}
                        </div>
                      </TableCell>

                      <TableCell className={creditCellClass}>
                        {formatQty(rowQty(row, "qty_in"))}
                      </TableCell>

                      <TableCell className={debitCellClass}>
                        {formatQty(rowQty(row, "qty_out"))}
                      </TableCell>

                      <TableCell className={moneyCellClass}>
                        {stockAfter(row)}
                      </TableCell>

                      <TableCell className="text-sm text-gray-500">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1.5">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDate(row.date)}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDateTime(row.created_at || row.date)}
                          </span>
                        </div>
                      </TableCell>

                      {actionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {row.kind === "entry" && (
                              <>
                                {canUpdate && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="સંપાદિત કરો"
                                    onClick={() => onEdit(row)}
                                  >
                                    <LuPencil className="h-4 w-4" />
                                  </Button>
                                )}

                                {canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="કાઢી નાખો"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => onDelete(row)}
                                  >
                                    <LuTrash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : rows.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label={emptyLabel} />
          </div>
        ) : (
          rows.map((row, index) => (
            <div key={`${row.kind}-${row.id}`} className={mobileCardClass}>
              <div className="flex items-start justify-between gap-3 border-b p-4">
                {typeHead(row)}
                <span className="shrink-0 text-sm text-gray-500">
                  ID: {row.id}
                </span>
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-semibold text-primary">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>વિગતો</span>
                  <span className="min-w-0 space-y-1.5 text-right">
                    {typeDetails(row)}
                    {extraText(row) && (
                      <span className="block break-words text-gray-600">
                        {extraText(row)}
                      </span>
                    )}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>આવ્યું</span>
                  <div className={creditCellClass}>
                    {formatQty(rowQty(row, "qty_in"))}
                  </div>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ગયું</span>
                  <div className={debitCellClass}>
                    {formatQty(rowQty(row, "qty_out"))}
                  </div>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>સ્ટોક</span>
                  <div className={moneyCellClass}>{stockAfter(row)}</div>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(row.date)}
                    <span className="block text-xs text-gray-400">
                      {formatDateTime(row.created_at || row.date)}
                    </span>
                  </span>
                </div>
              </div>

              {actionCount > 0 && row.kind === "entry" && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionCount === 1 ? "grid-cols-1" : "grid-cols-2"
                  )}
                >
                  {canUpdate && (
                    <Button
                      variant="outline"
                      title="સંપાદિત કરો"
                      onClick={() => onEdit(row)}
                    >
                      <LuPencil className="h-4 w-4" />
                    </Button>
                  )}

                  {canDelete && (
                    <Button
                      variant="outline"
                      title="કાઢી નાખો"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => onDelete(row)}
                    >
                      <LuTrash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default MaterialStockLedger;
